import { useLayoutEffect } from 'react';
import type { ReactElement } from 'react';

import { Header } from '@yanolja-org/core-ui';
import { DETECT_EXPRESSION, NolBridge } from '@yanolja-org/nol-bridge';

import type { HeaderLogoProps } from '@yanolja-org/core-ui';

// loser (webview → logo, browser → action icon) 에 `data-core-ui-hidden-static` 부착 → display:none + sibling 제외.
// inline script 는 elements 뒤에 두어 parse 후 sync 실행 (paint 전 CLS 0). React 미관리 attribute 라 재렌더에도 보존.
const SWAP_STYLE = `[data-core-ui-logo-or-action][data-core-ui-hidden-static]{display:none}`;
const DETECT_SCRIPT = `document.querySelectorAll('[data-core-ui-logo-or-action="'+((${DETECT_EXPRESSION})?'logo':'action')+'"]').forEach(e=>e.setAttribute('data-core-ui-hidden-static',''))`;

export interface LogoOrActionProps {
  /** Logo 케이스 — Header.Logo 의 logo prop pass-through. 미명시 시 bi widget 자동 fetch */
  logo?: HeaderLogoProps['logo'];
  /** Logo 케이스 — Header.Logo 의 href. SEO crawler 의 link graph 용도. @default '/' */
  logoHref?: string;
  /** Action 케이스 — Header.Action 의 action 식별자 */
  action: string;
  /** Action 케이스 — 웹뷰에서 표시할 아이콘 */
  icon: ReactElement;
  /** 공통 — className */
  className?: string;
}

// LogoOrBack / LogoOrClose 공유 base — NolBridge 감지 + inline script swap 로직 단일 유지.
// index.ts public surface 에는 노출 안 함 — preset 파일 간 내부 공유 전용.
export function LogoOrAction({ logo, logoHref = '/', action, icon, className }: LogoOrActionProps) {
  // 인라인 script 는 *initial HTML parse* 1회만 실행 — HMR 또는 React re-mount 가 일어나 새 DOM 노드가
  // 생성되면 attribute 가 없어져 둘 다 visible. 매 commit 후 sync 로 재적용해 정합성 유지 (idempotent).
  useLayoutEffect(() => {
    const loser = NolBridge.isEnabled ? 'logo' : 'action';
    document.querySelectorAll('[data-core-ui-logo-or-action]').forEach((el) => {
      el.toggleAttribute('data-core-ui-hidden-static', el.getAttribute('data-core-ui-logo-or-action') === loser);
    });
  });

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SWAP_STYLE }} />
      <Header.Action
        action={action}
        className={className}
        icon={icon}
        suppressHydrationWarning
        variant="icon"
        wrapperDataAttrs={{ 'data-core-ui-logo-or-action': 'action' }}
      />
      <Header.Logo
        className={className}
        data-core-ui-logo-or-action="logo"
        href={logoHref}
        logo={logo}
        suppressHydrationWarning
      />
      <script dangerouslySetInnerHTML={{ __html: DETECT_SCRIPT }} />
    </>
  );
}
